import { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Input,
  Button,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  useColorModeValue,
  useToast,
  Flex,
  Text,
  Link as ChakraLink,
  Spinner,
  Image,
} from "@chakra-ui/react";
import { Helmet } from "react-helmet-async";
import { useNavigate, Link, useSearchParams } from "react-router-dom";

import { supabase } from "../../utils/supabaseClient";
import { sendEmail } from "../../utils/emailClient";

export default function Signup() {
  const [params] = useSearchParams();
  const isBusiness = params.get("business") === "true";

  const toast = useToast();
  const navigate = useNavigate();

  const [referrerId, setReferrerId] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const urlRef = params.get("ref");
    const cookieRef = document.cookie
      .split("; ")
      .find((row) => row.startsWith("referrer_id="))
      ?.split("=")[1];

    if (urlRef) {
      setReferrerId(urlRef);
      document.cookie = `referrer_id=${urlRef}; path=/; max-age=604800`;
    } else if (cookieRef) {
      setReferrerId(cookieRef);
    }
  }, [params]);

  const handleSignup = async () => {
    setIsSubmitting(true);
    setErrorMsg("");

    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { businessName: name } },
      });

      if (authError) throw new Error(authError.message);
      const authUser = authData?.user;
      if (!authUser?.id) throw new Error("Auth user creation failed");

      const { data: business, error: bizError } = await supabase
        .from("businesses")
        .insert({
          auth_id: authUser.id,
          name,
          email,
          postcode: "PR1 2AB",
          credit_balance: 0.0,
          business_uuid: crypto.randomUUID(),
        })
        .select()
        .single();

      if (bizError || !business) throw new Error("Business creation failed");

      let logoUrl = null;

      if (logoFile) {
        const filePath = `logos/${business.id}-${logoFile.name}`;

        const { error: uploadError } = await supabase.storage
          .from("LOGOS")
          .upload(filePath, logoFile, { upsert: true });

        if (uploadError) throw new Error("Logo upload failed");

        const { data: publicUrlData } = supabase.storage
          .from("LOGOS")
          .getPublicUrl(filePath);

        logoUrl = publicUrlData.publicUrl;

        await supabase
          .from("businesses")
          .update({ logo_url: logoUrl })
          .eq("id", business.id);
      }

      const { error: profileError } = await supabase
        .from("user_profiles")
        .insert({
          id: authUser.id,
          user_role: "business",
          region: "UK",
          language: "en",
          moderation_role: "viewer",
        });

      if (profileError) throw new Error("User profile creation failed");

      if (referrerId) {
        const { data: existingReferral } = await supabase
          .from("referrals")
          .select("*")
          .eq("referred_email", email)
          .maybeSingle();

        if (!existingReferral) {
          await supabase.from("referrals").insert({
            referrer_id: parseInt(referrerId),
            referred_email: email,
          });
        }

        await supabase
          .from("businesses")
          .update({ credit_balance: supabase.raw("credit_balance + 5.00") })
          .eq("id", business.id);

        await supabase
          .from("referrals")
          .update({ bonus_awarded: true })
          .eq("referred_email", email);

        await supabase.rpc("assign_bonus_coupon", {
          business_id_input: parseInt(referrerId),
        });

        await supabase.rpc("assign_bonus_coupon", {
          business_id_input: business.id,
        });
      }

      await sendEmail({ to: email, kind: "welcome", name });
      await sendEmail({ to: email, kind: "businessWelcome", businessName: name });

      if (referrerId) {
        await sendEmail({
          to: email,
          kind: "referralBonus",
          referrerName: "Your Referrer",
        });
      }

      toast({
        title: "Signup successful",
        description: "Redirecting to dashboard...",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      navigate("/signup/success");
    } catch (err) {
      setErrorMsg(err.message);
      toast({
        title: "Signup failed",
        description: err.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignup = async () => {
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: "https://www.postcode.coupons/signup?business=true"
      },
    });
  };

  return (
    <>
      <Helmet>
        <title>{isBusiness ? "Business Signup" : "Sign Up"} | postcode.coupons</title>
      </Helmet>

      <Flex minH="100vh" align="center" justify="center" bg={useColorModeValue("gray.100", "gray.900")}>
        <Box bg={useColorModeValue("white", "gray.800")} p={8} rounded="md" shadow="lg" width="100%" maxW="md" position="relative">
          {isSubmitting && (
            <Flex position="absolute" top={0} left={0} right={0} bottom={0} bg="rgba(255,255,255,0.6)" zIndex={10} align="center" justify="center">
              <Spinner size="xl" color="red.500" />
            </Flex>
          )}

          <Flex justify="center" mb={4}>
            <Image src="https://www.postcode.coupons/logo512.png" alt="Postcode Coupons" boxSize="64px" />
          </Flex>

          <Heading mb={6} textAlign="center" color="red.600">
            📣 Business Signup
          </Heading>

          {errorMsg && (
            <Alert status="error" mb={4}>
              <AlertIcon />
              {errorMsg}
            </Alert>
          )}

          <FormControl mb={4} isRequired>
            <FormLabel>Business Name</FormLabel>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter your business name" />
          </FormControl>

          <FormControl mb={4}>
            <FormLabel>Business Logo (optional)</FormLabel>
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => setLogoFile(e.target.files[0])}
            />
          </FormControl>

          <FormControl mb={4} isRequired>
            <FormLabel>Email</FormLabel>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" placeholder="you@example.com" />
          </FormControl>

          <FormControl mb={4} isRequired>
            <FormLabel>Password</FormLabel>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" placeholder="••••••••" />
          </FormControl>

          <Button colorScheme="red" onClick={handleSignup} w="full" mt={2} isDisabled={isSubmitting || !email || !password || !name}>
            Sign Up
          </Button>

          <Button
            bg="white"
            color="black"
            border="1px solid #ddd"
            _hover={{ bg: "gray.100" }}
            w="full"
            mt={3}
            onClick={handleGoogleSignup}
          >
            <Image src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" boxSize="20px" mr={2} />
            Sign in with Google
          </Button>

          <Box textAlign="center" mt={4}>
            <Text>
              Already have an account?{" "}
              <ChakraLink as={Link} to="/login" color="red.500">
                Log in
              </ChakraLink>
            </Text>
          </Box>
        </Box>
      </Flex>
    </>
  );
}
